import {
    useState,
    useMemo
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type SelectBoxValue,
    SelectBox
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Callout,
    Preview,
    Section
} from "../../components/content";

type City = {
    name: string;
    id: string;
};

type Food = {
    category: string;
    name: string;
    id: string;
};

type SelectionItem = {
    __title: string;
};

const NAME = "SelectBox";

const USAGE_CODE = `const [city, setCity] = useState("");

<SelectBox
    onValueChange={(value) => setCity(value as string)}
    titleExtractor={(item) => item.name}
    keyExtractor={(item) => item.id}
    placeholder="Select a city"
    spreadBehaviour="stretch"
    isClearEnabled={true}
    title="City"
    data={cities}
    value={city}
/>`;

const MULTIPLE_CODE = `<SelectBox
    onOk={(items) => setCities(items.map((item) => item.__title))}
    titleExtractor={(item) => item.name}
    keyExtractor={(item) => item.id}
    isWorkWithRealtime={false}
    isMultipleSelect={true}
    title="Cities"
    maxChoice={3}
    data={cities}
/>`;

const CITIES: Array<City> = [
    {
        name: "İstanbul",
        id: "istanbul"
    },
    {
        name: "Ankara",
        id: "ankara"
    },
    {
        name: "İzmir",
        id: "izmir"
    },
    {
        name: "Bursa",
        id: "bursa"
    },
    {
        name: "Antalya",
        id: "antalya"
    },
    {
        name: "Adana",
        id: "adana"
    }
];

const SEARCH_CITIES: Array<City> = [
    ...CITIES,
    {
        name: "Konya",
        id: "konya"
    },
    {
        name: "Gaziantep",
        id: "gaziantep"
    },
    {
        name: "Mersin",
        id: "mersin"
    },
    {
        name: "Trabzon",
        id: "trabzon"
    }
];

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const GROUPS_CODE = `<SelectBox
    groupExtractor={(item) => item.category}
    titleExtractor={(item) => item.name}
    keyExtractor={(item) => item.id}
    spreadBehaviour="stretch"
    isSearchable={true}
    data={foods}
    title="Food"
/>`;

const FOOD_IDS = [
    "apple",
    "carrot",
    "banana",
    "spinach",
    "cherry",
    "broccoli"
] as const;

const MAX_CITIES = 3;

const getCityTitle = (city: City) => city.name;

const getCityKey = (city: City) => city.id;

const getFoodTitle = (food: Food) => food.name;

const getFoodKey = (food: Food) => food.id;

const getFoodGroup = (food: Food) => food.category;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("selectBoxPage.multipleTitle"),
        id: "multiple"
    },
    {
        title: t("selectBoxPage.searchTitle"),
        id: "search"
    },
    {
        title: t("selectBoxPage.groupsTitle"),
        id: "groups"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const SelectBoxPage = () => {
    const {
        t
    } = useDocsI18n();

    const foods = useMemo<Array<Food>>(() => {
        const categories = {
            vegetables: t("selectBoxPage.foodVegetables"),
            fruits: t("selectBoxPage.foodFruits")
        };

        const names = {
            broccoli: t("selectBoxPage.foodBroccoli"),
            spinach: t("selectBoxPage.foodSpinach"),
            banana: t("selectBoxPage.foodBanana"),
            carrot: t("selectBoxPage.foodCarrot"),
            cherry: t("selectBoxPage.foodCherry"),
            apple: t("selectBoxPage.foodApple")
        };

        const foodCategories = {
            broccoli: categories.vegetables,
            spinach: categories.vegetables,
            carrot: categories.vegetables,
            banana: categories.fruits,
            cherry: categories.fruits,
            apple: categories.fruits
        };

        return FOOD_IDS.map((id) => ({
            category: foodCategories[id],
            name: names[id],
            id
        }));
    }, [
        t
    ]);

    const [
        city,
        setCity
    ] = useState("");

    const [
        appliedCities,
        setAppliedCities
    ] = useState("");

    const handleCityChange = (nextValue: SelectBoxValue) => {
        setCity(String(nextValue));
    };

    const handleCitiesOk = (items: Array<SelectionItem>) => {
        setAppliedCities(items.map((item) => item.__title).join(", "));
    };

    return <Article>
        <ComponentHeader
            lead={t("selectBoxPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("selectBoxPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("selectBoxPage.overviewAlternatives")}
            </Paragraph>
            <Preview
                caption={t("selectBoxPage.overviewCaption")}
                isStacked={true}
            >
                <SelectBox
                    hintText={city ? t("selectBoxPage.selectedCity", [
                        CITIES.find((item) => item.id === city)?.name ?? city
                    ]) : undefined}
                    placeholder={t("selectBoxPage.cityPlaceholder")}
                    title={t("selectBoxPage.cityTitle")}
                    onValueChange={handleCityChange}
                    titleExtractor={getCityTitle}
                    keyExtractor={getCityKey}
                    spreadBehaviour="stretch"
                    isClearEnabled={true}
                    data={CITIES}
                    value={city}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
            <Callout>
                {t("selectBoxPage.dataNote")}
            </Callout>
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("selectBoxPage.multipleTitle")}
            id="multiple"
        >
            <Paragraph>
                {t("selectBoxPage.multipleText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <SelectBox
                    hintText={t("selectBoxPage.multipleHint", [
                        MAX_CITIES
                    ])}
                    title={t("selectBoxPage.citiesTitle")}
                    titleExtractor={getCityTitle}
                    keyExtractor={getCityKey}
                    spreadBehaviour="stretch"
                    isMultipleSelect={true}
                    maxChoice={MAX_CITIES}
                    data={CITIES}
                />
                <SelectBox
                    hintText={appliedCities ? t("selectBoxPage.applied", [
                        appliedCities
                    ]) : t("selectBoxPage.stagedHint")}
                    title={t("selectBoxPage.stagedTitle")}
                    titleExtractor={getCityTitle}
                    isWorkWithRealtime={false}
                    keyExtractor={getCityKey}
                    spreadBehaviour="stretch"
                    isMultipleSelect={true}
                    onOk={handleCitiesOk}
                    data={CITIES}
                />
            </Preview>
            <CodeBlock
                code={MULTIPLE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("selectBoxPage.searchTitle")}
            id="search"
        >
            <Paragraph>
                {t("selectBoxPage.searchText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <SelectBox
                    placeholder={t("selectBoxPage.searchPlaceholder")}
                    title={t("selectBoxPage.cityTitle")}
                    titleExtractor={getCityTitle}
                    keyExtractor={getCityKey}
                    spreadBehaviour="stretch"
                    data={SEARCH_CITIES}
                    isSearchable={true}
                />
            </Preview>
        </Section>
        <Section
            title={t("selectBoxPage.groupsTitle")}
            id="groups"
        >
            <Paragraph>
                {t("selectBoxPage.groupsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <SelectBox
                    placeholder={t("selectBoxPage.groupsPlaceholder")}
                    title={t("selectBoxPage.foodTitle")}
                    groupExtractor={getFoodGroup}
                    titleExtractor={getFoodTitle}
                    keyExtractor={getFoodKey}
                    spreadBehaviour="stretch"
                    isSearchable={true}
                    data={foods}
                />
            </Preview>
            <CodeBlock
                code={GROUPS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("selectBoxPage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <SelectBox
                        titleExtractor={getCityTitle}
                        keyExtractor={getCityKey}
                        spreadBehaviour="stretch"
                        defaultValue="ankara"
                        data={CITIES}
                        title={size}
                        size={size}
                        key={size}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("selectBoxPage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <SelectBox
                    title={t("selectBoxPage.disabled")}
                    titleExtractor={getCityTitle}
                    keyExtractor={getCityKey}
                    spreadBehaviour="stretch"
                    defaultValue="ankara"
                    isDisabled={true}
                    data={CITIES}
                />
                <SelectBox
                    title={t("selectBoxPage.readOnly")}
                    titleExtractor={getCityTitle}
                    keyExtractor={getCityKey}
                    spreadBehaviour="stretch"
                    defaultValue="ankara"
                    isReadOnly={true}
                    data={CITIES}
                />
                <SelectBox
                    title={t("selectBoxPage.loading")}
                    titleExtractor={getCityTitle}
                    keyExtractor={getCityKey}
                    spreadBehaviour="stretch"
                    isLoading={true}
                    data={CITIES}
                />
                <SelectBox
                    hintText={t("selectBoxPage.hintDanger")}
                    titleExtractor={getCityTitle}
                    keyExtractor={getCityKey}
                    spreadBehaviour="stretch"
                    isRequired={true}
                    title="danger"
                    data={CITIES}
                    type="danger"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("selectBoxPage.a11yTrigger"),
                    t("selectBoxPage.a11yList"),
                    t("selectBoxPage.a11yKeyboard"),
                    t("selectBoxPage.a11yTouch"),
                    t("selectBoxPage.a11yStates"),
                    t("selectBoxPage.a11yFeedback"),
                    t("selectBoxPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("selectBoxPage.propData"),
                        type: "Array<T>",
                        name: "data"
                    },
                    {
                        type: "(item: T & SelectedItem, index: number) => string",
                        description: t("selectBoxPage.propTitleExtractor"),
                        name: "titleExtractor"
                    },
                    {
                        type: "(item: T & SelectedItem, index: number) => string",
                        description: t("selectBoxPage.propKeyExtractor"),
                        name: "keyExtractor"
                    },
                    {
                        type: "(selectedItems: Array<SelectedItem>, data, setIsMoreLoading, setIsLoading) => void",
                        description: t("selectBoxPage.propOnChange"),
                        name: "onChange"
                    },
                    {
                        type: "(value: string | Array<string>, selectedItems: Array<SelectedItem>) => void",
                        description: t("selectBoxPage.propOnValueChange"),
                        name: "onValueChange"
                    },
                    {
                        description: t("selectBoxPage.propValue"),
                        type: "string | Array<string>",
                        name: "value"
                    },
                    {
                        description: t("selectBoxPage.propDefaultValue"),
                        type: "string | Array<string>",
                        name: "defaultValue"
                    },
                    {
                        description: t("selectBoxPage.propInitialSelectedItems"),
                        type: "Array<{ __title: string; __key: string }>",
                        name: "initialSelectedItems"
                    },
                    {
                        description: t("selectBoxPage.propIsMultipleSelect"),
                        name: "isMultipleSelect",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("selectBoxPage.propMaxChoice"),
                        defaultValue: "-1",
                        name: "maxChoice",
                        type: "number"
                    },
                    {
                        description: t("selectBoxPage.propMinChoice"),
                        name: "minChoice",
                        type: "number"
                    },
                    {
                        description: t("selectBoxPage.propIsWorkWithRealtime"),
                        name: "isWorkWithRealtime",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        type: "(selectedItems: Array<SelectedItem>, data, setIsMoreLoading, setIsLoading) => void",
                        description: t("selectBoxPage.propOnOk"),
                        name: "onOk"
                    },
                    {
                        type: "(selectedItems: Array<SelectedItem>, data, setIsMoreLoading, setIsLoading) => void",
                        description: t("selectBoxPage.propOnCancel"),
                        name: "onCancel"
                    },
                    {
                        description: t("selectBoxPage.propSelectValidation"),
                        type: "(selectedItem: SelectedItem) => boolean",
                        name: "selectValidation"
                    },
                    {
                        description: t("selectBoxPage.propRemoveSelectValidation"),
                        type: "(selectedItem: SelectedItem) => boolean",
                        name: "removeSelectValidation"
                    },
                    {
                        description: t("selectBoxPage.propIsSearchable"),
                        defaultValue: "false",
                        name: "isSearchable",
                        type: "boolean"
                    },
                    {
                        description: t("selectBoxPage.propGroupExtractor"),
                        type: "(item: T) => string",
                        name: "groupExtractor"
                    },
                    {
                        type: "({ data, selectedItems, searchText, setIsLoading, setIsMoreLoading }) => Array<T>",
                        description: t("selectBoxPage.propOnSearch"),
                        name: "onSearch"
                    },
                    {
                        type: "({ data, selectedItems, searchText, setIsLoading, setIsMoreLoading }) => void",
                        description: t("selectBoxPage.propOnMoreLoad"),
                        name: "onMoreLoad"
                    },
                    {
                        description: t("selectBoxPage.propMoreLoadThreshold"),
                        name: "moreLoadThreshold",
                        defaultValue: "0.85",
                        type: "number"
                    },
                    {
                        description: t("selectBoxPage.propRenderOptionIcon"),
                        type: "({ item, index }) => ReactElement",
                        name: "renderOptionIcon"
                    },
                    {
                        type: "({ item, index, isSelected, onSelect, ... }) => ReactElement",
                        description: t("selectBoxPage.propRenderItem"),
                        name: "renderItem"
                    },
                    {
                        description: t("selectBoxPage.propIsShowSelectSheetTools"),
                        name: "isShowSelectSheetTools",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("selectBoxPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("selectBoxPage.propPlaceholder"),
                        name: "placeholder",
                        type: "string"
                    },
                    {
                        description: t("selectBoxPage.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        type: "\"neutral\" | \"danger\" | \"warning\" | \"success\" | \"info\"",
                        description: t("selectBoxPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("selectBoxPage.propSize"),
                        name: "size"
                    },
                    {
                        description: t("selectBoxPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("selectBoxPage.propIsMinWidthEnabled"),
                        name: "isMinWidthEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("selectBoxPage.propIsRequired"),
                        defaultValue: "false",
                        name: "isRequired",
                        type: "boolean"
                    },
                    {
                        description: t("selectBoxPage.propIsOptional"),
                        name: "isOptional",
                        type: "boolean"
                    },
                    {
                        description: t("selectBoxPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("selectBoxPage.propIsShowSubTitle"),
                        name: "isShowSubTitle",
                        type: "boolean"
                    },
                    {
                        description: t("selectBoxPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("selectBoxPage.propIsReadOnly"),
                        defaultValue: "false",
                        name: "isReadOnly",
                        type: "boolean"
                    },
                    {
                        description: t("selectBoxPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("selectBoxPage.propIsClearEnabled"),
                        name: "isClearEnabled",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("selectBoxPage.propIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("selectBoxPage.propIconOnPress"),
                        name: "iconOnPress",
                        type: "() => void"
                    },
                    {
                        description: t("selectBoxPage.propIconAccessibilityLabel"),
                        name: "iconAccessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("selectBoxPage.propRightIcon"),
                        type: "NCoreUIKitIcon",
                        name: "rightIcon"
                    },
                    {
                        description: t("selectBoxPage.propRightIconOnPress"),
                        name: "rightIconOnPress",
                        type: "() => void"
                    },
                    {
                        description: t("selectBoxPage.propRightIconAccessibilityLabel"),
                        name: "rightIconAccessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("selectBoxPage.propHintTextIcon"),
                        type: "NCoreUIKitIcon",
                        name: "hintTextIcon"
                    },
                    {
                        description: t("selectBoxPage.propIsShowHintTextIcon"),
                        name: "isShowHintTextIcon",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("selectBoxPage.propOnFocus"),
                        type: "() => void",
                        name: "onFocus"
                    },
                    {
                        description: t("selectBoxPage.propOnBlur"),
                        type: "() => void",
                        name: "onBlur"
                    },
                    {
                        description: t("selectBoxPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("selectBoxPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("selectBoxPage.propsNote")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("selectBoxPage.refNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default SelectBoxPage;
